/**
 * Warehouse-related types shared between backend and frontend.
 */

/**
 * Allowed values for `im_warehouses.type`. Application-level enum — no DB-level
 * ENUM or CHECK constraint. Validated at the contract layer (`z.enum`) and at
 * the service layer when narrowing values read out of the DB.
 */
export const WAREHOUSE_TYPE = {
  HUB: "hub",
  BRANCH: "branch",
} as const;

export type WarehouseType =
  (typeof WAREHOUSE_TYPE)[keyof typeof WAREHOUSE_TYPE];

/**
 * Tuple of values, suitable for `z.enum(WAREHOUSE_TYPE_VALUES)` and for
 * iterating in UI dropdowns. Keep in sync with `WAREHOUSE_TYPE`.
 */
export const WAREHOUSE_TYPE_VALUES = [
  WAREHOUSE_TYPE.HUB,
  WAREHOUSE_TYPE.BRANCH,
] as const satisfies readonly WarehouseType[];

export function isWarehouseType(value: unknown): value is WarehouseType {
  return WAREHOUSE_TYPE_VALUES.includes(value as WarehouseType);
}

/**
 * Allowed values for `im_warehouse_hierarchy.relationship_type`. Application-level
 * enum — no DB-level ENUM or CHECK constraint. `primary_hub` is the dominant
 * sourcing relationship for a child warehouse; `sibling_branch` covers
 * peer-to-peer transfer eligibility within a division.
 */
export const WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE = {
  PRIMARY_HUB: "primary_hub",
  SIBLING_BRANCH: "sibling_branch",
} as const;

export type WarehouseHierarchyRelationshipType =
  (typeof WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE)[keyof typeof WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE];

export const WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE_VALUES = [
  WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE.PRIMARY_HUB,
  WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE.SIBLING_BRANCH,
] as const satisfies readonly WarehouseHierarchyRelationshipType[];

export function isWarehouseHierarchyRelationshipType(
  value: unknown,
): value is WarehouseHierarchyRelationshipType {
  return WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE_VALUES.includes(
    value as WarehouseHierarchyRelationshipType,
  );
}
